import React, {useMemo, useState} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {clsx} from "clsx";
import {apollo} from "../../utils/apollo";
import {useQuery} from "react-query";
import Loading from "../../components/Loading";
import {useTranslation} from "react-i18next";
import {GET_MERCHANT_SUPPLIERS_LIST, GET_PAYMENT_SUMMARY_LIST} from "../../utils/graphql";
import Pagination from "../../components/Pagination";
import PageHeader from "../../components/PageHeader";
import SortIcon from "../../components/SortIcon";
import {formatNumberInt, formatNumberFloat} from "../../utils/helpers";
import SelectInput from "../../components/SelectInput";
import useAuthStore from "../../store/auth";
import {CalendarIcon, ChartBarIcon, CreditCardIcon, ArrowUpIcon, ArrowDownIcon} from '@heroicons/react/24/outline';
import Button from "../../components/Button";

const START_YEAR = 2024;
const YEARS = [...Array(new Date().getFullYear() - START_YEAR + 1)];

const CURRENT_MONTH = `0${new Date().getMonth() + 1}`.slice(-2);
const CURRENT_YEAR = new Date().getFullYear().toString();

function Summary() {
	const {t} = useTranslation();
	const {merchant} = useAuthStore();
	const [sort, setSort] = useState({field: 'id', isAscending: false});
	const [page, setPage] = useState(1);
 const [showFilter, setShowFilter] = useState(true);
	const [filter, setFilter] = useState({status: '', test: '', supplier: null})
	const [date, setDate] = useState({month: CURRENT_MONTH, year: CURRENT_YEAR});

	const tableHeader = [
		{column: 'date', title: t('summary.table.date'), sortable: false},
		{column: 'received_amount', title: t('summary.table.received_amount'), sortable: false},
		{column: 'total_count', title: t('summary.table.total_count'), sortable: false},
		{column: 'pending_count', title: t('summary.table.pending_count'), sortable: false},
		{column: 'completed_count', title: t('summary.table.completed_count'), sortable: false},
		{column: 'failed_count', title: t('summary.table.failed_count'), sortable: false},
		{column: 'expired_count', title: t('summary.table.expired_count'), sortable: false},
		{column: 'refunded_count', title: t('summary.table.refunded_count'), sortable: false},
		{column: 'canceled_count', title: t('summary.table.canceled_count'), sortable: false},
		{column: 'on_hold_count', title: t('summary.table.on_hold_count'), sortable: false},
	]
	const handleChangeSort = (field) => {
		if (sort.field === field) {
			setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
		} else {
			setSort({field: field, isAscending: true});
		}
	};
	const fetchPaymentSummary = useQuery(
		['payment_summary', {date, filter}],
		async () => {
			const {data} = await apollo.query({
				query: GET_PAYMENT_SUMMARY_LIST,
				variables: {
					month: date?.month || CURRENT_MONTH,
					year: date?.year || CURRENT_YEAR,
					supplier: filter.supplier,
				},
			});
			return data?.getPaymentSummaryList;
		},
		{keepPreviousData: false},
	);

    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false
    });

	const records = fetchPaymentSummary?.isSuccess ? fetchPaymentSummary : null;
    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;

	return (
		<DashboardLayout>
			<div>
				<div className="mb-6">
					<div className="flex items-center gap-3 mb-2">
						<div className="p-2 rounded-lg bg-gradient-to-br from-sky-500 to-teal-500 shadow-sm">
							<CreditCardIcon className="h-6 w-6 text-white" />
						</div>
						<h2 className="text-2xl font-bold text-gray-900">{t('summary.page_title')}</h2>
					</div>
					<p className="text-gray-600 mt-1">{t('summary.page_description')}</p>
				</div>

				<div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8">
					<div className="px-6 py-4 bg-gradient-to-br from-sky-500 to-teal-500 border-b border-gray-200 rounded-t-xl">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<div className="p-2 rounded-lg bg-white/20">
									<CalendarIcon className="h-5 w-5 text-white" />
								</div>
								<h3 className="text-lg font-medium text-white">{t('summary.filter_options')}</h3>
							</div>
							<button 
								disabled
								className="text-xs font-medium text-white bg-white/20 opacity-50 cursor-not-allowed px-3 py-1 rounded-full"
							>
								{t('summary.hide_filters')}
							</button>
						</div>
					</div>
					<div className="p-6">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
							<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
								<SelectInput 
									name="supplier-filter" 
									label="Supplier" 
									id="supplier-filter" 
									get={filter.supplier}
									set={v => setFilter({...filter, supplier: v})}
									className="w-full"
								>
									<option value="">{t('summary.all_suppliers')}</option>
									{suppliersList?.map(supplier => <option key={supplier.supplierUuid} value={supplier.supplierUuid}>{supplier.supplierName}</option>)}
								</SelectInput>
							</div>
							<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
								<SelectInput 
									name="month-filter" 
									label="Month" 
									id="month-filter" 
									get={date.month} 
									set={v => setDate({...date, month: v})}
									className="w-full"
								>
									<option value="01">{t('summary.months.january')}</option>
									<option value="02">{t('summary.months.february')}</option>
									<option value="03">{t('summary.months.march')}</option>
									<option value="04">{t('summary.months.april')}</option>
									<option value="05">{t('summary.months.may')}</option>
									<option value="06">{t('summary.months.june')}</option>
									<option value="07">{t('summary.months.july')}</option>
									<option value="08">{t('summary.months.august')}</option>
									<option value="09">{t('summary.months.september')}</option>
									<option value="10">{t('summary.months.october')}</option>
									<option value="11">{t('summary.months.november')}</option>
									<option value="12">{t('summary.months.december')}</option>
								</SelectInput>
							</div>
							<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
								<SelectInput 
									name="year-filter" 
									label="Year" 
									id="year-filter" 
									get={date.year} 
									set={v => setDate({...date, year: v})}
									className="w-full"
								>
									{YEARS.map((_, i) => <option key={i} value={START_YEAR + i}>{START_YEAR + i}</option>)}
								</SelectInput>
							</div>
						</div>
					</div>
				</div>

				{fetchPaymentSummary.isLoading && (
					<div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
						<div className="flex justify-center items-center">
							<div className="relative">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="h-16 w-16 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 opacity-20 animate-ping"></div>
								</div>
								<div className="relative flex items-center justify-center">
									<div className="h-16 w-16 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 flex items-center justify-center">
										<CreditCardIcon className="h-8 w-8 text-white" />
									</div>
								</div>
							</div>
						</div>
						<h3 className="mt-6 text-lg font-semibold text-gray-900">{t('summary.loading_title')}</h3>
						<p className="mt-2 text-sm text-gray-500">{t('summary.loading_description')}</p>
					</div>
				)}

				{(fetchPaymentSummary.isSuccess && !records?.data?.length) && (
					<div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
						<div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
							<ChartBarIcon className="h-8 w-8 text-gray-400" />
						</div>
						<h3 className="mt-6 text-lg font-semibold text-gray-900">{t('summary.no_data_title')}</h3>
						<p className="mt-2 text-sm text-gray-500">{t('summary.no_data_description')}</p>
						<div className="mt-6">
							<Button
								onClick={() => fetchPaymentSummary.refetch()} 
							>
								{t('summary.refresh_data')}
							</Button>
						</div>
					</div>
				)}

				{(fetchPaymentSummary.isSuccess && records?.data?.length > 0) && (
					<div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
						<div className="px-6 py-4 bg-gradient-to-br from-sky-500 to-teal-500 border-b border-gray-200 rounded-t-xl">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<div className="p-2 rounded-lg bg-white/20">
										<CreditCardIcon className="h-5 w-5 text-white" />
									</div>
									<h3 className="text-lg font-medium text-white">{t('summary.settlement_summary')}</h3>
								</div>
								<span className="text-xs font-medium text-white bg-white/20 px-3 py-1 rounded-full">
									{records?.data?.length || 0} {t('summary.records')}
								</span>
							</div>
						</div>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										{tableHeader?.map(item => (
											<th 
												key={item.column} 
												scope="col" 
												className="px-6 py-3.5 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider"
											>
												{!item.hidden && (
													<>
														{item.sortable ? (
															<button 
																onClick={() => handleChangeSort(item.column)} 
																type="button" 
																className="group inline-flex items-center"
															>
																{item.title}
																<SortIcon sort={sort} field={item.column}/>
															</button>
														) : (
															<span>{item.title}</span>
														)}
													</>
												)}
											</th>
										))}
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{records?.data?.map((record) => (
										<tr key={record?.date} className="hover:bg-gray-50 transition-colors">
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{record?.date}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex items-center">
													<div className="text-sm font-bold text-blue-600">
														{formatNumberFloat(record?.receivedAmount)} {merchant?.currency || ''}
													</div>
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{formatNumberInt(record?.totalCount)}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.pendingCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
															{formatNumberInt(record?.pendingCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.completedCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
															{formatNumberInt(record?.completedCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.failedCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
															{formatNumberInt(record?.failedCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.expiredCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
															{formatNumberInt(record?.expiredCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.refundedCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
															{formatNumberInt(record?.refundedCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.canceledCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
															{formatNumberInt(record?.canceledCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{record?.onHoldCount > 0 ? (
													<div className="flex items-center">
														<div className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
															{formatNumberInt(record?.onHoldCount)}
														</div>
													</div>
												) : (
													<div className="text-sm text-gray-500">0</div>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>

		</DashboardLayout>
	);
}

export default Summary;
